import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "../../../hooks/useWallet";
import LnUrl from "./components/LnUrl";
import { QRCodeCanvas } from "qrcode.react";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { useTheme } from "../../../hooks/useTheme";
import { ButtonVariant } from "../../../config/colors";
import { Button, ButtonSize } from "../../../components/Button";
import { WalletType } from "../../../models/Transactions";
import Loader from "../components/Loader";
import OnChainAddress from "./components/OnChainAddress";

type ReceiveTab = "lightning" | "onchain";

interface ReceiveTransactionProps {}

const ReceiveTransaction: React.FC<ReceiveTransactionProps> = () => {
    const { lnurl, getOnchainAddress, walletType } = useWallet();
    const navigate = useNavigate();
    const { isMobile, isDarkMode } = useTheme();
    const [selectedTab, setSelectedTab] = useState<ReceiveTab>("lightning");
    const [onchainAddress, setOnchainAddress] = useState<string | null>(null);
    const [loadingOnchainAddress, setLoadingOnchainAddress] = useState(false);
    const didRequestOnchainRef = useRef(false);

    const showTabs = walletType === WalletType.SPARK;

    useEffect(() => {
        if (!showTabs || selectedTab !== "onchain" || didRequestOnchainRef.current) {
            return;
        }

        didRequestOnchainRef.current = true;
        setLoadingOnchainAddress(true);

        getOnchainAddress()
            .then((address) => {
                setOnchainAddress(address);
            })
            .catch((error) => {
                console.error("Failed to load on-chain address:", error);
            })
            .finally(() => {
                setLoadingOnchainAddress(false);
            });
    }, [getOnchainAddress, selectedTab, showTabs]);

    const onClickPrevArrow = () => {
        if (isMobile) {
            return () => navigate(`/home/wallet`);
        }

        return undefined;
    };

    const activeAddress = selectedTab === "lightning" ? lnurl : onchainAddress;
    const qrValue =
        selectedTab === "lightning"
            ? lnurl
                ? `lightning:${lnurl}`
                : ""
            : onchainAddress
              ? `bitcoin:${onchainAddress}`
              : "";

    const renderQrCode = () => {
        if (selectedTab === "onchain" && loadingOnchainAddress) {
            return (
                <div className="flex items-center justify-center">
                    <Loader small />
                </div>
            );
        }

        if (!qrValue) {
            return (
                <div className="flex items-center justify-center text-center text-sm text-brand-text-2 px-4">
                    {selectedTab === "onchain"
                        ? "Unable to load on-chain address"
                        : "No Lightning address available"}
                </div>
            );
        }

        return (
            <>
                <QRCodeCanvas
                    value={qrValue}
                    size={280}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                    level="H"
                    style={{
                        maxWidth: "100%",
                        height: "auto",
                        backgroundColor: "#FFFFFF",
                        filter: "none",
                    }}
                />
                <img
                    src="/logo192.png"
                    alt="Logo"
                    className="absolute w-[60px] h-[60px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-white p-1"
                />
            </>
        );
    };

    return (
        <LayoutWithTopBar
            breadcrumbs={[
                { title: "Wallet", onClick: () => navigate("/home/wallet") },
                { title: "Receive" },
            ]}
            onClickPrevArrow={onClickPrevArrow()}
        >
            <div className="flex-1 pt-4 px-4 flex flex-col justify-center items-center gap-6 max-w-[400px] w-full mx-auto">
                {showTabs && (
                    <div className="w-full max-w-md">
                        <div className="flex p-1 bg-brand-bg-2 rounded-2xl gap-1">
                            {(["lightning", "onchain"] as ReceiveTab[]).map((tab) => (
                                <Button
                                    key={tab}
                                    variant={
                                        selectedTab === tab
                                            ? ButtonVariant.inverted
                                            : ButtonVariant.transparent
                                    }
                                    size={ButtonSize.medium}
                                    className="flex-1"
                                    onClick={() => setSelectedTab(tab)}
                                >
                                    {tab === "lightning" ? "Lightning" : "On-chain"}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}

                {selectedTab === "onchain" && (
                    <p className="text-sm text-center text-brand-text-2 max-w-md">
                        On-chain transactions arrive in{" "}
                        <span className="text-brand-orange">~10 minutes</span>
                    </p>
                )}

                <div className="bg-brand-bg-2 rounded-lg p-4 flex flex-col gap-4 w-full">
                    <div className="flex justify-center w-full max-w-[280px] aspect-square mx-auto">
                        <div className="bg-brand-white rounded-lg p-4 flex justify-center items-center relative aspect-square">
                            {renderQrCode()}
                        </div>
                    </div>

                    {activeAddress ? (
                        selectedTab === "lightning" ? (
                            <LnUrl lnurl={activeAddress} className="justify-center" />
                        ) : (
                            <OnChainAddress address={activeAddress} className="justify-center" />
                        )
                    ) : null}
                </div>

                {selectedTab === "lightning" && (
                    <div className="flex flex-col items-center gap-4">
                        <Button
                            size={ButtonSize.medium}
                            variant={ButtonVariant.semiTransparent}
                            className="text-brand-text-2 text-sm"
                            onClick={() => {
                                navigate("/home/wallet/receive/create-invoice/amount");
                            }}
                        >
                            Create invoice
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex flex-col items-center p-4 mb-2">
                {isDarkMode ? (
                    <img src={"/images/logo_text.svg"} alt="" className="w-[150px]" />
                ) : (
                    <img src={"/images/logo_text_light.svg"} alt="" className="w-[150px]" />
                )}
            </div>
        </LayoutWithTopBar>
    );
};

export default ReceiveTransaction;
